'use client';

import Link from 'next/link';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mantine/core';
import classes from './FlyingTextVideo.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';

type FlyingTextVideoProps = {
  title: string;
  description: string;
  imageUrl: string;
  videoUrl?: string;      // YouTube/Vimeo URL or direct .mp4/.webm/.ogg (may be proxied)
  frameUrl?: string;
  showButton?: boolean;
  buttonText?: string;
  buttonHref?: string;

  /** Controls */
  autoplay?: boolean;     // controls inline + modal autoplay for file videos
  startMuted?: boolean;   // default true
  hoverControls?: boolean;// custom controls bar on hover (file videos only)
  lightbox?: boolean;     // when true, show image + play overlay; open modal on click
};

const getUnderlyingUrl = (input?: string) => {
  if (!input) return undefined;
  try {
    // support relative paths like /api/image-proxy?url=...
    const u = new URL(input, typeof window !== 'undefined' ? window.location.origin : 'http://local');
    const nested = u.searchParams.get('url');
    return nested ? decodeURIComponent(nested) : input;
  } catch {
    const m = input?.match(/(?:\?|&)url=([^&#]+)/);
    return m ? decodeURIComponent(m[1]) : input;
  }
};

const hasVideoExt = (s?: string) => /\.(mp4|webm|ogg)(?:$|[?#])/i.test(s || '');

const isDirectVideo = (url?: string) => {
  const original = getUnderlyingUrl(url);
  if (!original) return false;
  try {
    const { pathname } = new URL(original);
    return /\.(mp4|webm|ogg)$/i.test(pathname);
  } catch {
    return hasVideoExt(original);
  }
};

const toYouTubeEmbed = (url: string) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) {
        return `https://www.youtube.com/embed/${v}?autoplay=1&mute=1&rel=0&modestbranding=1&loop=1&playlist=${v}&controls=0`;
      }
    }
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace('/', '');
      if (id) {
        return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0&modestbranding=1&loop=1&playlist=${id}&controls=0`;
      }
    }
  } catch {}
  return url;
};

const toVimeoEmbed = (url: string) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      if (id) {
        return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&background=1&loop=1&title=0&byline=0&portrait=0`;
      }
    }
  } catch {}
  return url;
};

const buildEmbedUrl = (url?: string) => {
  const original = getUnderlyingUrl(url);
  if (!original) return undefined;
  if (original.includes('youtu')) return toYouTubeEmbed(original);
  if (original.includes('vimeo')) return toVimeoEmbed(original);
  return undefined;
};

const FlyingTextVideo: React.FC<FlyingTextVideoProps> = ({
  title,
  description,
  imageUrl,
  videoUrl,
  frameUrl,
  showButton = false,
  buttonText = 'En savoir plus',
  buttonHref = '#',

  autoplay = true,
  startMuted = true,
  hoverControls = true,
  lightbox = false,
}) => {
  useAOS();

  const isFileVideo = isDirectVideo(videoUrl);
  const embedUrl = buildEmbedUrl(videoUrl);

  // ---------- Lightbox / modal ----------
  const [opened, setOpened] = useState(false);
  const openModal = () => setOpened(true);
  const closeModal = () => setOpened(false);

  // ---------- Custom controls (file videos only) ----------
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const [playing, setPlaying] = useState<boolean>(autoplay && !lightbox);
  const [muted, setMuted] = useState<boolean>(startMuted);
  const [volume, setVolume] = useState<number>(0.7);

  // keep internal state aligned if props change
  useEffect(() => {
    setPlaying(autoplay && !lightbox);
  }, [autoplay, lightbox]);

  useEffect(() => {
    setMuted(startMuted);
    if (videoRef.current) videoRef.current.muted = startMuted;
  }, [startMuted]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const onVolumeChange = (val: number) => {
    const v = videoRef.current;
    const clamped = Math.max(0, Math.min(1, val));
    setVolume(clamped);
    if (v) {
      v.volume = clamped;
      if (clamped > 0 && v.muted) {
        v.muted = false;
        setMuted(false);
      }
    }
  };

  const shouldAutoplayInline = useMemo(() => autoplay && !lightbox, [autoplay, lightbox]);

  return (
    <section className={classes.blockWrapper}>
      <h2 className={classes.title} data-aos="fade-down">{title}</h2>

      <div className={classes.gridLayout}>
        {/* LEFT: text */}
        <div className={classes.leftSide} data-aos="fade">
          <div className={classes.textContent}>
            <h2 className={classes.titleMobile}>{title}</h2>
            <p className={classes.description}>{description}</p>
            {showButton && (
              <Link href={buttonHref} className={classes.button} data-aos="fade-up" data-aos-delay="100">
                {buttonText}
              </Link>
            )}
          </div>
        </div>

        {/* RIGHT: media */}
        <div className={classes.rightSide}>
          <div className={classes.videoWrapper} data-aos="fade-down" data-aos-delay="100">
            {frameUrl && <img src={frameUrl} alt="" aria-hidden className={classes.fissureFrame} />}

            {/* LIGHTBOX MODE: image with play overlay, click opens modal */}
            {lightbox ? (
              <button
                type="button"
                className={classes.thumbButton}
                onClick={openModal}
                aria-label="Lire la vidéo dans une fenêtre"
              >
                <img className={classes.thumbImage} src={imageUrl} alt={title} />
                <span className={classes.playOverlay} />
              </button>
            ) : embedUrl ? (
              // Inline EMBED (YT/Vimeo). Custom hover controls don't apply to iframes.
              <iframe
                className={classes.videoFrame}
                src={embedUrl}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                title={title}
              />
            ) : isFileVideo ? (
              // Inline FILE VIDEO with custom hover controls
              <div
                className={classes.inlineVideoHost}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <video
                  ref={videoRef}
                  className={classes.videoFrame}
                  src={videoUrl}              // ensure your proxy supports Range if proxied
                  poster={imageUrl || undefined}
                  autoPlay={shouldAutoplayInline}
                  muted={startMuted}
                  loop
                  playsInline
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                />
                {hoverControls && (
                  <div className={`${classes.controls} ${hovered ? classes.controlsVisible : ''}`}>
                    <button
                      type="button"
                      className={classes.ctrlBtn}
                      onClick={togglePlay}
                      aria-label={playing ? 'Pause' : 'Lire'}
                    >
                      {playing ? '❚❚' : '►'}
                    </button>
                    <button
                      type="button"
                      className={classes.ctrlBtn}
                      onClick={toggleMute}
                      aria-label={muted ? 'Activer le son' : 'Couper le son'}
                    >
                      {muted ? '🔇' : '🔊'}
                    </button>
                    <input
                      className={classes.volume}
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={volume}
                      onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                      aria-label="Volume"
                    />
                  </div>
                )}
              </div>
            ) : (
              <img className={classes.imageFallback} src={imageUrl} alt={title} />
            )}
          </div>
        </div>
      </div>

      {/* MODAL PLAYER */}
      <Modal
        opened={opened}
        onClose={closeModal}
        centered
        size="xl"
        withCloseButton
        overlayProps={{ opacity: 0.5, blur: 2 }}
      >
        <div className={classes.modalVideoHost}>
          {embedUrl ? (
            <iframe
              className={classes.modalFrame}
              src={embedUrl.replace('controls=0', 'controls=1')}
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              title={title}
            />
          ) : isFileVideo ? (
            <video
              className={classes.modalFrame}
              src={videoUrl}
              poster={imageUrl || undefined}
              autoPlay={autoplay}
              muted={startMuted}
              loop
              controls
              playsInline
            />
          ) : (
            <img className={classes.modalFrame} src={imageUrl} alt={title} />
          )}
        </div>
      </Modal>
    </section>
  );
};

export default FlyingTextVideo;
